/* eslint-disable react/display-name */
import React from 'react'
import { Controller } from 'react-hook-form'
import styled from 'styled-components'
import { isNumeric, mergeRefs, useId } from '@mote/helpers'
import { mergeClassnames } from '@mote/helpers/dom'
import { Select } from '../Select/Select'
import { Input } from '../input/input'
import { TextArea } from '../textarea/textarea'
import {
  FormFeedbackMessage,
  FormGroup,
  FormHelpMessage,
  FormLabel
} from './layout'

function parseInputValue(value) {
  return isNumeric(value) ? Number(value) : value
}

export const FormInput = React.forwardRef(
  (
    {
      id: idProp,
      name,
      className,
      type = 'input',
      label,
      helpText,
      required = true,
      disabled = false,
      rootProps = {},
      inputProps = {},
      labelProps = {},
      helpTextProps = {},
      feedbackProps = {},
      control,
      shouldUnregister = false,
      onChange,
      parseValue = true,
      placeholder
    },
    ref
  ) => {
    const uuid = useId()
    const id = idProp || `field-${uuid}`
    const labelId = `${id}-label`
    const helpId = `${id}-help`
    const feedbackId = `${id}-feedback`

    const { Input, getInputValue } = React.useMemo(() => {
      if (type === 'select')
        return {
          Input: StyledSelect,
          getInputValue: value => ({
            currentValue: value.toString()
          })
        }

      return {
        Input: type === 'textarea' ? StyledTextArea : StyledInput,
        getInputValue: value => ({ value: value.toString() })
      }
    }, [type])

    return (
      <FormGroup
        {...rootProps}
        className={mergeClassnames(
          'form-control',
          className,
          rootProps.className
        )}
        role='group'
      >
        {label && (
          <FormLabel
            required={required}
            {...labelProps}
            id={labelProps.id ?? labelId}
            htmlFor={labelProps.htmlFor ?? id}
            className={mergeClassnames(
              'form-input__label',
              labelProps.className
            )}
          >
            {label}
          </FormLabel>
        )}
        <Controller
          control={control}
          name={name}
          rules={{ required, disabled }}
          shouldUnregister={shouldUnregister}
          render={({
            field: {
              onChange: onFormValueChange,
              value,
              name,
              ref: controlRef
            },
            fieldState: { invalid, error }
          }) => {
            const mergedProps = { ...inputProps, ...getInputValue(value) }

            return (
              <>
                <Input
                  {...mergedProps}
                  ref={mergeRefs(controlRef, ref)}
                  id={id}
                  name={name}
                  placeholder={placeholder || mergedProps.placeholder}
                  invalid={invalid}
                  disabled={disabled}
                  className={mergeClassnames(
                    'form-input',
                    inputProps.className
                  )}
                  aria-describedby={
                    invalid && error?.message ? feedbackId : null
                  }
                  onChange={event => {
                    const { value } = event.target
                    const parsedValue = parseValue
                      ? parseInputValue(value)
                      : value

                    const customEvent = {
                      target: {
                        value: parsedValue
                      },
                      nativeEvent: event
                    }

                    onChange?.(customEvent)
                    onFormValueChange(parsedValue)
                  }}
                />
                {helpText && (
                  <FormHelpMessage
                    {...helpTextProps}
                    id={helpId}
                    className={mergeClassnames(
                      'form-input__help-text',
                      helpTextProps.className
                    )}
                  >
                    {helpText}
                  </FormHelpMessage>
                )}
                {invalid && error?.message && (
                  <FormFeedbackMessage
                    {...feedbackProps}
                    id={feedbackId}
                    className={mergeClassnames(
                      'form-input__error-message',
                      feedbackProps.className
                    )}
                    aria-live='polite'
                    isError
                  >
                    {error.message}
                  </FormFeedbackMessage>
                )}
              </>
            )
          }}
        />
      </FormGroup>
    )
  }
)

export const StyledSelect = styled(Select)``

export const StyledInput = styled(Input)``

export const StyledTextArea = styled(TextArea)`
  background: #ffffff;
`
